import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check, ArrowRight, WhatsappLogo, Envelope, Car } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VehicleQuotePage = ({ vehicleId, vehicleName, vehicleImage, vehicleDescription, features, specs }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service_type: `devis_${vehicleId}`, message: "", preferred_contact: "email",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success("Demande envoyee !");
        setFormData({ name: "", email: "", phone: "", service_type: `devis_${vehicleId}`, message: "", preferred_contact: "email" });
      }
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid={`vehicle-page-${vehicleId}`}>
      <SEO title={vehicleName} path={`/vehicules/${vehicleId.replace('_', '-')}`} description={`${vehicleName} avec chauffeur privé à Paris — ALTURA. ${vehicleDescription.slice(0, 120)}`} />
      {/* Hero with vehicle image */}
      <section className="py-16 sm:py-24 bg-[#EDEBE4]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">Sur devis</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight mb-6">
                {vehicleName}
              </h1>
              <div className="w-16 h-px bg-[#1A1A1A] mb-6" />
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">{vehicleDescription}</p>
              <ul className="space-y-3 mb-8">
                {features.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#1A1A1A]/80 text-sm">
                    <Check size={18} weight="bold" className="text-[#1A1A1A] flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <img src={vehicleImage} alt={vehicleName} className="w-full rounded-none" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Specs */}
      {specs && (
        <section className="py-12 bg-[#E5E3DC]">
          <div className="max-w-7xl mx-auto px-6 sm:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {specs.map((spec, i) => (
                <div key={i} className="bg-white border border-[#1A1A1A]/10 p-6 text-center">
                  <p className="font-heading text-2xl text-[#1A1A1A] mb-1">{spec.value}</p>
                  <p className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide">{spec.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quote form */}
      <section className="py-16 sm:py-24 bg-[#EDEBE4]">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="bg-white border border-[#1A1A1A]/10 p-8 lg:p-12">
              <h2 className="font-heading text-2xl text-[#1A1A1A] mb-2">Demande de devis</h2>
              <p className="text-[#1A1A1A]/70 text-sm mb-8">Decrivez votre besoin et nous vous proposerons une offre sur mesure pour le {vehicleName}.</p>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid={`quote-form-${vehicleId}`}>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Nom complet *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder="Votre nom" data-testid="input-name" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder="votre@email.com" data-testid="input-email" />
                  </div>
                </div>
                <div>
                  <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Telephone (WhatsApp)</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder="+33 6 12 34 56 78" data-testid="input-phone" />
                </div>
                <div>
                  <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Mode de contact prefere</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="preferred_contact" value="email" checked={formData.preferred_contact === "email"} onChange={handleChange} className="accent-[#1A1A1A]" />
                      <Envelope size={18} className="text-[#1A1A1A]/60" /><span className="text-[#1A1A1A]/80 text-sm">Email</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="preferred_contact" value="whatsapp" checked={formData.preferred_contact === "whatsapp"} onChange={handleChange} className="accent-[#1A1A1A]" />
                      <WhatsappLogo size={18} className="text-[#1A1A1A]/60" /><span className="text-[#1A1A1A]/80 text-sm">WhatsApp</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">Votre demande *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className="input-luxury w-full bg-transparent resize-none" placeholder="Decrivez votre besoin : date, trajet, nombre de passagers, duree..." data-testid="input-message" />
                </div>
                <button type="submit" disabled={loading} className="btn-dark w-full flex items-center justify-center gap-3 disabled:opacity-50" data-testid="submit-quote">
                  {loading ? "Envoi..." : "Envoyer ma demande"}
                  <ArrowRight size={16} weight="bold" />
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

// ===== Range Rover Autobiography =====
export const RangeRoverPage = () => (
  <VehicleQuotePage
    vehicleId="range_rover"
    vehicleName="Range Rover Autobiography"
    vehicleImage="https://customer-assets.emergentagent.com/job_parisgo/artifacts/g4j06n8c_EA53DE21-6D52-41A3-8649-CA99B77FA055.png"
    vehicleDescription="Le Range Rover Autobiography incarne le summum du luxe tout-terrain. Confort absolu, presence imposante et raffinement interieur font de ce vehicule le choix ideal pour les deplacements d'exception."
    features={[
      "Siege massant et chauffant en cuir pleine fleur",
      "Suspension pneumatique adaptative",
      "Systeme audio Meridian Signature",
      "Refrigerateur integre",
      "Isolation phonique renforcee",
      "Toit panoramique",
    ]}
    specs={[
      { value: "3", label: "Places" },
      { value: "V8", label: "Motorisation" },
      { value: "4x4", label: "Transmission" },
      { value: "Premium", label: "Confort" },
    ]}
  />
);

// ===== Mercedes Classe G =====
export const ClasseGPage = () => (
  <VehicleQuotePage
    vehicleId="classe_g"
    vehicleName="Mercedes Classe G"
    vehicleImage="https://customer-assets.emergentagent.com/job_parisgo/artifacts/nvf2ktbp_A218CC23-6605-4A15-A797-D5667A10ABE1.png"
    vehicleDescription="Le Mercedes Classe G est une icone intemporelle. Son design reconnaissable entre tous et ses performances tout-terrain en font un vehicule de prestige unique pour des deplacements au caractere affirme."
    features={[
      "Design iconique et intemporel",
      "Interieur cuir Nappa bi-ton",
      "Systeme audio Burmester surround",
      "Ecrans MBUX haute resolution",
      "Capacites tout-terrain legendaires",
      "Presence et charisme uniques",
    ]}
    specs={[
      { value: "3", label: "Places" },
      { value: "AMG", label: "Finition" },
      { value: "4x4", label: "Transmission" },
      { value: "Iconique", label: "Style" },
    ]}
  />
);

// ===== Mercedes-Maybach Classe S =====
export const MaybachPage = () => (
  <VehicleQuotePage
    vehicleId="maybach"
    vehicleName="Mercedes-Maybach Classe S"
    vehicleImage="https://customer-assets.emergentagent.com/job_parisgo/artifacts/f97xw5lg_994950FD-BC1F-48CB-AEDC-7B53591C370A.png"
    vehicleDescription="La Mercedes-Maybach Classe S represente l'excellence absolue du transport de luxe. Empattement long, finitions exclusives et confort de premiere classe en font le vehicule de reference pour les plus exigeants."
    features={[
      "Empattement long pour un espace arriere exceptionnel",
      "Sieges Executive arriere inclinables a 43.5 degres",
      "Ciel de toit etoile Maybach",
      "Refrigerateur a champagne integre",
      "Isolation phonique de reference",
      "Finitions exclusives Maybach",
    ]}
    specs={[
      { value: "2", label: "Places VIP" },
      { value: "V12", label: "Motorisation" },
      { value: "1st Class", label: "Confort" },
      { value: "Maybach", label: "Prestige" },
    ]}
  />
);
